/**
 *
 * TabTeachers
 *
 */

import React, { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

// styles
import './style.scss';

// antd component
import { Form } from 'antd';

// assets
import CustomIcons from 'components/CustomIcons';

// components
import CustomTable from 'components/CustomTable';
import AddItemCard from 'components/AddItemCard';

import SelectFieldAxios from 'containers/SelectFieldAxios';

// utils
import {
  openNewRow,
  starRowDataActions,
  confirmSaveRowData,
  confirmRemoveRowData,
} from 'components/Tabs/helper.js';

// helper for forms
import { sortList } from 'utils/helpers/helper.general';

function TabTeachers({
  teachers,
  sourceData,
  // actions
  updateSourse,
  // default
  className,
}) {
  const childClassNames = classNames(
    'tab-teachers-wrapper row-tabs',
    className,
  );

  const initSort = {
    ascend: true, // descend
    props: ['description.lastname'],
  };

  const [currentTeachers, setCurrentTeachers] = useState(teachers || []);
  const [currentSourse, setCurrentSourse] = useState(sourceData || false);

  const [tableRows, setTableRow] = useState([]);
  const [newElement, setNewElement] = useState([]);
  const [showError, setShowError] = useState(false);

  // Form initialization for new row
  const [addNewFrom] = Form.useForm();

  // Set data for opened item
  useEffect(() => {
    setCurrentSourse(sourceData);
  }, [sourceData]);

  useEffect(() => {
    const sortedTeachers = teachers ? sortList(teachers, initSort) : [];
    setCurrentTeachers(sortedTeachers);

    const rows =
      sortedTeachers?.length > 0 &&
      sortedTeachers?.map(elem => {
          return {
            ...elem,
            firstname: elem?.description?.firstname,
            lastname: elem?.description?.lastname,
          };
        })
        .sort((a, b) => a?.lastname.localeCompare(b?.lastname));
    setTableRow(rows);
    setCurrentSourse(sourceData);
  }, []);

  // Set data for opened item
  useEffect(() => {
    // set teachers list
    if (JSON.stringify(currentTeachers) !== JSON.stringify(teachers)) {
      const sortedTeachers = teachers ? sortList(teachers, initSort) : [];
      setCurrentTeachers(sortedTeachers);
      const rows = sortedTeachers.map(elem => {
        return {
          ...elem,
          firstname: elem?.description?.firstname,
          lastname: elem?.description?.lastname,
        };
      }).sort((a, b) => a?.lastname.localeCompare(b?.lastname));
      setTableRow(rows);
    }
  }, [teachers, currentTeachers]);

  // create new Row with form
  const makeNewRow = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: 'New Teacher',
      name: 'new_teacher',
      endponit: 'users/role',
      endponitSend: {
        role: 'teacher',
      },
      required: true,
      displayLabel: false,
      value: '',
      type: 'axios-select',
      col: 24,
      onSelect: (LabeledValue, option) => {
        setNewElement(option?.item);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${item?.description?.firstname}`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <Form form={addNewFrom} name="add-new-element" autoComplete="off">
        <SelectFieldAxios
          className="table-select"
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          itemCallback={newElementSelect.itemCallback}
          onSelect={newElementSelect.onSelect}
          item={newElementSelect}
        />
        {showError && <span className="alert">Cet est déjà sur la liste.</span>}
      </Form>
    );
  }, [addNewFrom, showError, setNewElement]);

  // Remove and update current Element
  const removeElement = elementObj => {
    if (elementObj && elementObj._id) {
      const newElements =
        currentTeachers.length > 0 &&
        currentTeachers.filter(el => el._id !== elementObj._id);

      setCurrentTeachers(newElements);
      let source = { ...currentSourse };
      if (newElements && newElements.length <= 0) {
        source = {
          ...source,
          teachers: newElements,
          responsible: null,
        };
      } else {
        source = {
          ...source,
          teachers: newElements,
        };
      }
      setCurrentSourse(source);
      updateSourse(source);
    }
  };

  // save/add new Element form new Row
  const addElement = () => {
    addNewFrom.submit();
    const val = addNewFrom.getFieldsValue();
    if (val.new_teacher && newElement) {
      const hasElement =
        currentTeachers &&
        currentTeachers.some(element => element._id === newElement._id);

      if (!hasElement) {
        setShowError(false);
        const newEl = {
          _id: newElement?._id,
          description: newElement?.description,
        };
        const newEls = [newEl, ...currentTeachers];
        const sortedTeachers = newEls ? sortList(newEls, initSort) : [];
        setCurrentTeachers(sortedTeachers);

        const source = { ...currentSourse, teachers: sortedTeachers };
        setCurrentSourse(source);
        updateSourse(source);
      } else {
        setShowError(true);
      }
    }
  };

  let setResponsible = record => {
    if (
      record &&
      record.hasOwnProperty('role') &&
      record.role === 'teacher'
    ) {
      const source = {
        ...currentSourse,
        responsible: record,
        teachers: currentTeachers,
      };
      setCurrentSourse(source);
      updateSourse(source);
    }
  };

  const actionsColumn = {
    title: '',
    dataIndex: 'action',
    key: 'action-column-teacher',
    className: 'column-actions',
    render: (_, record) => {
      if (record.key === 'initial') {
        return (
          <div className="row-actions">
            {confirmSaveRowData(tableRows, setTableRow, addElement)}
          </div>
        );
      } else {
        const isResponsible = record?._id === currentSourse?.responsible?._id;

        const upadateRowAction = {
          record: record,
          rows: tableRows,
          setRows: setTableRow,
          confirmHandle: setResponsible,
          checked: isResponsible,
        };
        return (
          <div className="row-actions">
            {starRowDataActions(upadateRowAction)}
            {confirmRemoveRowData(record, removeElement)}
          </div>
        );
      }
    },
  };

  const columns = [
    {
      title: (
        <div className="table-title">
          <CustomIcons type="person" />{' '}
          <span className="name">Enseignant(e)</span>
        </div>
      ),
      dataIndex: 'teacher',
      key: 'column-teacher',
      className: 'column-teacher',
      sorter: (a, b) => a?.lastname.localeCompare(b?.lastname),
      render: (text, record) => {
        if (record.key === 'initial') {
          return {
            children: makeNewRow,
            props: {
              colSpan: 1,
            },
          };
        } else {
          const isResponsible =
            record?._id === currentSourse?.responsible?._id;
          return {
            children: (
              <>
                <Link className="link-table" to={`/utilisateurs/${record._id}`}>
                  {record?.lastname} {record?.firstname}
                </Link>
                {isResponsible && (
                  <span className="additional-info">Responsable du cours</span>
                )}
              </>
            ),
            props: {
              colSpan: 1,
              className: isResponsible && 'responsible',
            },
          };
        }
      },
      //onFilter: (value, record) => record.firstname.indexOf(value) === 0,
      // sorter: (a, b) => a.lastname.length - b.lastname.length,
    },
  ];

  // Set action column object
  columns.push(actionsColumn);


  // Render
  return (
    <div className={childClassNames}>
      <div className="row">
        <AddItemCard
          type="section"
          title="Enseignant(s)"
          addAction={() => openNewRow(tableRows, setTableRow, addNewFrom)}
        />
      </div>
      <div className="row">
        <CustomTable
          dataSource={tableRows || []}
          columns={columns}
        />
      </div>
    </div>
  );
}

TabTeachers.propTypes = {
  teachers: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  sourceData: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
  ]),
  updateSourse: PropTypes.func.isRequired,
};

export default memo(TabTeachers);
